/**
 * PlaylistMetadataEditor Component
 * 
 * Comprehensive form component for creating and editing playlist metadata including:
 * - Basic information (title, description, language, author)
 * - Thumbnail URL
 * - Content type restriction settings
 * - Tag management
 * - Publishing status management
 * - Form validation and error handling
 * - Real-time thumbnail preview
 * 
 * Used in: PlaylistList component for metadata editing
 */
import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Grid,
    MenuItem,
    FormControlLabel,
    Checkbox,
    Chip,
    Alert,
    Avatar
} from '@mui/material';
import { Save, Cancel, Add } from '@mui/icons-material';
import { userRequest } from '../../requestMethods';
import PageContainer from '../layout/PageContainer';

const PlaylistMetadataEditor = ({ playlist, onSave, onCancel }) => {
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        language: '',
        author: '',
        thumbUrl: '',
        contentType: 'mixed',
        tags: [],
        isReadyToServe: false
    });

    const [languages, setLanguages] = useState([]);
    const [tagInput, setTagInput] = useState('');
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    const contentTypes = [
        { value: 'mixed', label: 'Mixed Content', description: 'Allow all content types' },
        { value: 'video', label: 'Video Only', description: 'Only video content allowed' },
        { value: 'text', label: 'Text Only', description: 'Only text content (stories, articles) allowed' },
        { value: 'playlist', label: 'Playlists Only', description: 'Only other playlists allowed' }
    ];

    useEffect(() => {
        fetchLanguages();
        if (playlist) {
            setFormData({
                title: playlist.title || '',
                description: playlist.description || '',
                language: playlist.language?._id || playlist.language || '',
                author: playlist.author || '',
                thumbUrl: playlist.thumbUrl || '',
                contentType: playlist.contentType || 'mixed',
                tags: playlist.tags || [],
                isReadyToServe: playlist.isReadyToServe || false
            });
        }
    }, [playlist]);

    const fetchLanguages = async () => {
        try {
            const response = await userRequest.get('/language');
            setLanguages(response.data);
        } catch (error) {
            console.error('Error fetching languages:', error);
        }
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        
        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    const handleAddTag = () => {
        const tag = tagInput.trim();
        if (tag && !formData.tags.includes(tag)) {
            setFormData(prev => ({
                ...prev,
                tags: [...prev.tags, tag]
            }));
            setTagInput('');
            
            // Clear tags error
            if (errors.tags) {
                setErrors(prev => ({
                    ...prev,
                    tags: ''
                }));
            }
        }
    };

    const handleRemoveTag = (tagToRemove) => {
        setFormData(prev => ({
            ...prev,
            tags: prev.tags.filter(tag => tag !== tagToRemove)
        }));
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddTag();
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.title.trim()) {
            newErrors.title = 'Title is required';
        }

        if (!formData.language) {
            newErrors.language = 'Language is required';
        }

        if (formData.tags.length === 0) {
            newErrors.tags = 'At least one tag is required';
        }

        if (!formData.contentType) {
            newErrors.contentType = 'Content type is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        setLoading(true);
        
        try {
            if (playlist) {
                // Update existing playlist
                await userRequest.patch(`/contentPlaylist/${playlist._id}`, formData);
            } else {
                // Create new playlist
                await userRequest.post('/contentPlaylist', formData);
            }
            
            onSave();
        } catch (error) {
            console.error('Error saving playlist:', error);
            
            if (error.response?.data?.details) {
                // Handle validation errors from server
                const serverErrors = {};
                error.response.data.details.forEach(detail => {
                    if (detail.includes('title')) serverErrors.title = detail;
                    if (detail.includes('language')) serverErrors.language = detail;
                    if (detail.includes('tags')) serverErrors.tags = detail;
                    if (detail.includes('contentType')) serverErrors.contentType = detail;
                });
                setErrors(serverErrors);
            } else {
                setErrors({ general: error.response?.data?.error || 'Failed to save playlist' });
            }
        }
        
        setLoading(false);
    };

    const isEditing = Boolean(playlist);

    return (
        <PageContainer title={isEditing ? 'Edit Playlist' : 'Create New Playlist'}>
            <Paper sx={{ p: 3 }}>
                <form onSubmit={handleSubmit}>
                    {errors.general && (
                        <Alert severity="error" sx={{ mb: 3 }}>
                            {errors.general}
                        </Alert>
                    )}

                    <Grid container spacing={3}>
                        {/* Basic Information */}
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom>
                                Basic Information
                            </Typography>
                        </Grid>

                        <Grid item xs={12} md={8}>
                            <TextField
                                label="Title"
                                value={formData.title}
                                onChange={(e) => handleInputChange('title', e.target.value)}
                                error={Boolean(errors.title)}
                                helperText={errors.title}
                                fullWidth
                                required
                            />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <TextField
                                select
                                label="Language"
                                value={formData.language}
                                onChange={(e) => handleInputChange('language', e.target.value)}
                                error={Boolean(errors.language)}
                                helperText={errors.language}
                                fullWidth
                                required
                            >
                                {languages.map((lang) => (
                                    <MenuItem key={lang._id} value={lang._id}>
                                        {lang.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                label="Description"
                                value={formData.description}
                                onChange={(e) => handleInputChange('description', e.target.value)}
                                multiline
                                rows={3}
                                fullWidth
                                placeholder="Enter a description for your playlist..."
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Author"
                                value={formData.author}
                                onChange={(e) => handleInputChange('author', e.target.value)}
                                fullWidth
                                placeholder="Enter author name"
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Thumbnail URL"
                                value={formData.thumbUrl}
                                onChange={(e) => handleInputChange('thumbUrl', e.target.value)}
                                fullWidth
                                placeholder="https://example.com/image.jpg"
                            />
                        </Grid>

                        {/* Thumbnail Preview */}
                        {formData.thumbUrl && (
                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Typography variant="body2">Thumbnail Preview:</Typography>
                                    <Avatar
                                        src={formData.thumbUrl}
                                        alt="Thumbnail"
                                        variant="rounded"
                                        sx={{ width: 80, height: 60 }}
                                    />
                                </Box>
                            </Grid>
                        )}

                        {/* Content Type Settings */}
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                                Content Restrictions
                            </Typography>
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                select
                                label="Content Type"
                                value={formData.contentType}
                                onChange={(e) => handleInputChange('contentType', e.target.value)}
                                error={Boolean(errors.contentType)}
                                helperText={errors.contentType || 'This determines what types of content can be added to this playlist'}
                                fullWidth
                                required
                            >
                                {contentTypes.map((type) => (
                                    <MenuItem key={type.value} value={type.value}>
                                        <Box>
                                            <Typography variant="body1">{type.label}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {type.description}
                                            </Typography>
                                        </Box>
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        {/* Tags */}
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                                Tags
                            </Typography>
                        </Grid>

                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                                {formData.tags.map((tag, index) => (
                                    <Chip
                                        key={index}
                                        label={tag}
                                        onDelete={() => handleRemoveTag(tag)}
                                        color="primary"
                                        variant="outlined"
                                    />
                                ))}
                            </Box>
                            
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <TextField
                                    label="Add Tag"
                                    value={tagInput}
                                    onChange={(e) => setTagInput(e.target.value)}
                                    onKeyPress={handleKeyPress}
                                    error={Boolean(errors.tags)}
                                    helperText={errors.tags}
                                    sx={{ flexGrow: 1 }}
                                    placeholder="Enter tag and press Enter"
                                />
                                <Button
                                    variant="outlined"
                                    onClick={handleAddTag}
                                    startIcon={<Add />}
                                    disabled={!tagInput.trim()}
                                >
                                    Add
                                </Button>
                            </Box>
                        </Grid>

                        {/* Publishing Status */}
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                                Publishing Status
                            </Typography>
                        </Grid>

                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={formData.isReadyToServe}
                                        onChange={(e) => handleInputChange('isReadyToServe', e.target.checked)}
                                    />
                                }
                                label="Ready to serve (visible to users)"
                            />
                        </Grid>

                        {/* Action Buttons */}
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
                                <Button
                                    variant="outlined"
                                    onClick={onCancel}
                                    startIcon={<Cancel />}
                                    disabled={loading}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    startIcon={<Save />}
                                    disabled={loading}
                                >
                                    {loading ? 'Saving...' : (isEditing ? 'Update Playlist' : 'Create Playlist')}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </form>
            </Paper>
        </PageContainer>
    );
};

export default PlaylistMetadataEditor;